/**
 * RecentDistributionsTable - Tabela premium com distribuições recentes em tempo real
 * 
 * Design com gradiente, ícone decorativo, badge animado e efeito shimmer
 * 
 * Exibe para cada distribuição:
 * - Timestamp
 * - Contato
 * - Departamento
 * - Agente atribuído
 * - Status (sucesso/falha)
 * 
 * @version 1.1.0
 * @date 2025-12-21
 */

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCircle, XCircle, AlertTriangle, User, Users, Building2, Activity, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { RecentDistribution } from '@/hooks/useDistributionMonitor';

// ==============================================================
// Interfaces
// ==============================================================

interface RecentDistributionsTableProps {
  /** Lista de distribuições recentes */
  distributions: RecentDistribution[];
  /** Indica se está carregando */
  isLoading?: boolean;
}

// ==============================================================
// Componente Principal
// ==============================================================

export function RecentDistributionsTable({ distributions, isLoading }: RecentDistributionsTableProps) {
  // Estado de loading com design premium
  if (isLoading) {
    return (
      <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 transition-all duration-300 hover:shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        
        <div className="relative flex items-center gap-3 p-4 border-b border-border/50">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500/10">
            <Activity className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Distribuições Recentes</h3>
            <p className="text-xs text-muted-foreground">Atualizando em tempo real</p>
          </div>
        </div>
        
        <div className="p-4 space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-14 bg-muted/50 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  // Estado vazio
  if (distributions.length === 0) {
    return (
      <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 transition-all duration-300 hover:shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
        
        <div className="relative flex items-center gap-3 p-4 border-b border-border/50">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500/10">
            <Activity className="h-5 w-5 text-emerald-500" />
          </div>
          <h3 className="font-semibold text-foreground">Distribuições Recentes</h3>
        </div>
        
        <div className="p-12 text-center">
          <Clock className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Nenhuma distribuição automática registrada ainda
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative overflow-hidden rounded-xl border bg-gradient-to-br from-emerald-500/5 to-emerald-500/10 transition-all duration-300 hover:shadow-lg">
      {/* Efeito shimmer no hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
      
      {/* Header premium */}
      <div className="relative flex items-center justify-between p-4 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500/10 transition-transform duration-300 group-hover:scale-110">
            <Activity className="h-5 w-5 text-emerald-500" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Distribuições Recentes</h3>
            <p className="text-xs text-muted-foreground">Últimas 20 distribuições</p>
          </div>
        </div>
        
        {/* Badge de tempo real com animação de pulse */}
        <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          Tempo real
        </Badge>
      </div>
      
      {/* Lista de distribuições */}
      <ScrollArea className="h-[420px]">
        <div className="relative divide-y divide-border/30">
          {distributions.map((dist, index) => (
            <div
              key={dist.id}
              className={cn(
                'flex items-center gap-4 p-4 transition-all duration-200 hover:bg-emerald-500/5',
                index === 0 && 'animate-in fade-in slide-in-from-top-2 duration-300 bg-emerald-500/5'
              )}
            >
              {/* Ícone de status */}
              <div
                className={cn(
                  'p-2.5 rounded-full shrink-0 transition-transform duration-200 hover:scale-110',
                  dist.success 
                    ? 'bg-emerald-500/10 text-emerald-500' 
                    : 'bg-destructive/10 text-destructive'
                )}
              >
                {dist.success ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
              </div>

              {/* Informações principais */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Contato ou Grupo - com indicador visual de tipo */}
                  <div className={cn(
                    "flex items-center gap-1.5 rounded-full px-2 py-0.5",
                    dist.conversationType === 'group' 
                      ? "bg-blue-50 text-blue-700" 
                      : "bg-muted/50"
                  )}>
                    {dist.conversationType === 'group' ? (
                      <>
                        <Users className="h-3 w-3 text-blue-500" />
                        <span className="text-sm font-medium truncate max-w-[130px]">
                          {dist.groupName || 'Grupo'}
                        </span>
                      </>
                    ) : (
                      <>
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm font-medium truncate max-w-[130px]">
                          {dist.contactName || dist.contactPhone || 'Contato'}
                        </span>
                      </>
                    )}
                  </div>

                  <span className="text-muted-foreground">→</span>

                  {/* Agente ou falha */}
                  {dist.success ? (
                    <span className="text-sm text-emerald-600 font-semibold truncate max-w-[120px]">
                      {dist.agentName || 'Agente'}
                    </span>
                  ) : (
                    <span className="text-sm text-destructive flex items-center gap-1 font-medium">
                      <AlertTriangle className="h-3 w-3" />
                      Sem agente
                    </span>
                  )}
                </div>

                {/* Departamento e método */}
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Building2 className="h-3 w-3" />
                    <span>{dist.departmentName}</span>
                  </div>
                  
                  <Badge 
                    variant="outline" 
                    className="text-[10px] px-1.5 py-0 bg-muted/50 border-muted"
                  >
                    {dist.distributionMethod === 'round_robin' ? 'Round Robin' : 
                     dist.distributionMethod === 'least_busy' ? 'Menos Ocupado' : 
                     dist.distributionMethod}
                  </Badge>

                  {dist.eligibleAgentsCount === 0 && !dist.success && (
                    <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                      0 agentes elegíveis
                    </Badge>
                  )}
                </div>

                {/* Motivo da falha se houver */}
                {dist.failureReason && (
                  <p className="text-xs text-destructive mt-1.5 bg-destructive/10 px-2 py-1 rounded">
                    {dist.failureReason}
                  </p>
                )}
              </div>

              {/* Timestamp e tempo de processamento */}
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-foreground">
                  {format(new Date(dist.createdAt), 'HH:mm', { locale: ptBR })}
                </p>
                <p className={cn(
                  'text-xs',
                  dist.processingTimeMs < 100 
                    ? 'text-emerald-500' 
                    : dist.processingTimeMs < 500 
                    ? 'text-amber-500' 
                    : 'text-destructive'
                )}>
                  {dist.processingTimeMs}ms
                </p>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
